import { Checkbox, FormControlLabel } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useRef, useState } from 'react';
import {
  ASSET_TYPE_PROPERTY_UI_BUILDER,
  ASSET_TYPE_PROPERTY,
  ASSET_HOTELS,
} from '../../../constants/PropertyConst';
import { ASSET_URL, ENVIRONMENT, PROPERTY_SERVICE_URL } from '../../../env/Configurations';
import { getPreSignedUrl } from '../../../utils/PropertyAPIUtils';
import './Image-Picker.scss';
import { notificationManager } from '../../../utils/NotificationUtils';
import {
  abortMultipartUpload,
  calculateFileChecksum,
  calculateTotalChunks,
  CHUNK_SIZE,
  completeMultipartUpload,
  convertFileToBase64,
} from '../../../utils/commonUtils';

const ImagePicker = (props) => {
  const {
    ratio,
    height,
    width,
    matrix,
    title,
    onImageSelect,
    onShadowOverlaySelect,
    imgURL,
    hotelId,
    shadowOverlay,
    groupId,
    imageDetail,
    error,
    isCustomImage = false,
    format = 'PNG',
    setIsSwapEnabled = () => {},
  } = props;
  const [isSizeExceeded, setSizeExceeded] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dimensionError, setDimensionError] = useState(false);
  const [fileFormatError, setFileFormatError] = useState(false);
  const [namingError, setNamingError] = useState(false);
  const inputFile = useRef(null);

  const onImageSelectorClick = () => {
    inputFile?.current?.click();
  };

  const removeImage = () => {
    onImageSelect(null);
  };

  const handleImageSelect = async (e) => {
    const fileObj = e.target.files[0];
    const fileSize = fileObj?.size;
    setIsSwapEnabled(true);
    if (fileObj) {
      if (isCustomImage && fileObj.type !== 'image/png' && fileObj.type !== 'image/svg+xml') {
        setUploadError(false);
        setNamingError(false);
        setDimensionError(false);
        setFileFormatError(true);
        removeImage();
      } else if (
        fileObj.type === 'image/png' ||
        fileObj.type === 'image/jpeg' ||
        fileObj.type === 'image/svg+xml'
      ) {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
          var imageObj = new Image();
          imageObj.src = reader.result;
          imageObj.onload = async () => {
            if (imageObj.height > height || imageObj.width > width) {
              setDimensionError(true);
              setFileFormatError(false);
              return removeImage();
            }
            setDimensionError(false);
            if (fileObj.size / 1024 / 1024 < 10) {
              setIsUploading(true);
              setSizeExceeded(false);
              setFileFormatError(false);
              if (fileSize / 1024 / 1024 > 2) {
                const totalChunks = calculateTotalChunks(fileSize);
                let uploadId = null;
                const fileChecksum = await calculateFileChecksum(fileObj);

                let input = {
                  type: ASSET_TYPE_PROPERTY_UI_BUILDER,
                  groupId: groupId,
                  propertyType: 'ui-builder',
                  contentLength: fileSize,
                  contentType: fileObj?.type,
                  filename: fileObj?.name,
                };

                if (isCustomImage) {
                  input = {
                    type: ASSET_TYPE_PROPERTY,
                    groupId: groupId,
                    propertyType: ASSET_HOTELS,
                    contentLength: fileSize,
                    contentType: fileObj?.type,
                    filename: fileObj?.name,
                  };
                }

                try {
                  const startUploadResponse = await fetch(
                    `${PROPERTY_SERVICE_URL}/startMultipartUpload`,
                    {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        hotelId,
                        groupId,
                        type: input.type,
                        propertyType: input.propertyType,
                        contentType: input.contentType,
                        filename: input.filename,
                        IsCMSRequest: true,
                        fileSize,
                        fileChecksum,
                      }),
                    },
                  );

                  if (!startUploadResponse.ok) {
                    throw new Error(`Failed to start upload: ${startUploadResponse.status}`);
                  }

                  const chunkData = await startUploadResponse.json();
                  uploadId = chunkData?.data?.key;

                  try {
                    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                      const start = chunkIndex * CHUNK_SIZE;
                      const end = Math.min(start + CHUNK_SIZE, fileSize);
                      const fileChunk = fileObj?.slice(start, end);
                      const base64Chunk = await convertFileToBase64(fileChunk);
                      const response = await fetch(
                        `${PROPERTY_SERVICE_URL}/uploadMultipartChunk`,
                        {
                          method: 'POST',
                          headers: {
                            Referer: 'http://localhost:3000/',
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            groupId,
                            hotelId,
                            uploadId,
                            sequenceNumber: chunkIndex + 1,
                            contents: base64Chunk,
                          }),
                        },
                      );

                      if (!response.ok) {
                        throw new Error(
                          `Failed to upload chunk ${chunkIndex + 1}: ${response.status}`,
                        );
                      }
                    }

                    const completeResponse = await completeMultipartUpload(
                      groupId,
                      hotelId,
                      uploadId,
                    );

                    if (
                      completeResponse?.status === 'ok' &&
                      completeResponse?.data?.status === true
                    ) {
                      const imageUrl = completeResponse?.data?.message;
                      if (imageUrl) {
                        onImageSelect(imageUrl);
                        setIsUploading(false);
                        setUploadError(false);
                      }
                    } else {
                      console.error('File upload failed:', completeResponse);
                      throw new Error('Failed to complete upload');
                    }
                  } catch (error) {
                    console.error('Error during file upload:', error);
                    setUploadError(true);

                    if (uploadId) {
                      await abortMultipartUpload(uploadId, groupId, hotelId);
                    }
                  }
                } catch (error) {
                  console.error('Error in multipart upload:', error);
                  setUploadError(true);
                }
              } else {
                convertFileToBase64(fileObj)
                  .then(async (base64) => {
                    let input = {
                      type: ASSET_TYPE_PROPERTY_UI_BUILDER,
                      groupId: groupId,
                      propertyType: 'ui-builder',
                      contentLength: fileObj.size,
                      contentType: fileObj.type,
                      filename: fileObj.name,
                      contents: base64,
                    };

                    if (isCustomImage) {
                      input = {
                        type: ASSET_TYPE_PROPERTY,
                        groupId: groupId,
                        propertyType: ASSET_HOTELS,
                        contentLength: fileObj.size,
                        contentType: fileObj.type,
                        filename: fileObj.name,
                        skipContentValidation: false,
                        contents: base64,
                      };
                    }

                    try {
                      const res = await getPreSignedUrl(input);

                      if (res?.data?.getPreSignUrlCMS?.key) {
                        onImageSelect(res.data.getPreSignUrlCMS.key);
                        setIsUploading(false);
                        setUploadError(false);
                        setNamingError(false);
                        setFileFormatError(false);
                      } else {
                        setUploadError(true);
                        setIsUploading(false);
                        setFileFormatError(false);
                      }
                    } catch (exception) {
                      if (ENVIRONMENT === 'dev') {
                        console.log(exception?.message);
                      }

                      if (exception?.message) {
                        notificationManager(
                          'danger',
                          exception?.message.replace('GraphQL error:', ''),
                        );
                      } else {
                        notificationManager('danger', 'Error while uploading file.');
                      }
                      setIsUploading(false);
                      setFileFormatError(false);
                    }
                  })
                  .catch((error) => {
                    if (ENVIRONMENT === 'dev') {
                      console.log(error?.message, 'error');
                    }

                    notificationManager('danger', 'Error converting file to base64');
                  });
              }
            } else {
              setSizeExceeded(true);
            }
          };
        });
        reader.readAsDataURL(e.target.files[0]);
      } else {
        setUploadError(false);
        setNamingError(false);
        setDimensionError(false);
        setFileFormatError(true);
        removeImage();
      }
    }
  };

  return (
    <div className='col-12 image-picker'>
      <div className='col-12 mb-3'>
        <span className='title'>{title ? title : 'Image'}</span>
      </div>
      <div className='row'>
        <div className='col-4'>
          <div className='img-drop-zone'>
            {isUploading ? (
              <div className='d-flex align-items-center img-select justify-content-center'>
                <CircularProgress />
              </div>
            ) : (
              <div
                onClick={onImageSelectorClick}
                className='d-flex align-items-center img-select justify-content-center'
              >
                {imgURL ? (
                  <div className={`image-container ${shadowOverlay ? 'shadowed-img' : ''}`}>
                    <img src={ASSET_URL + imgURL} alt='SelectedImage' />
                  </div>
                ) : (
                  <>
                    <Add fontSize='large' />
                  </>
                )}
                <input
                  type='file'
                  onChange={handleImageSelect}
                  accept={!isCustomImage ? 'image/png, image/jpeg, image/svg+xml' : 'image/png'}
                  id='file'
                  ref={inputFile}
                  style={{ display: 'none' }}
                />
              </div>
            )}
          </div>
          <div className='d-flex row px-2'>
            {isSizeExceeded && (
              <span className='size-exceeded mt-3'>The maximum file size is 10mb</span>
            )}
            {uploadError && !error && (
              <span className='size-exceeded mt-3'>Image uploading failed</span>
            )}
            {namingError && !error && <span className='size-exceeded mt-3'>Rename image file</span>}
            {dimensionError && !error && (
              <span className='size-exceeded mt-3'>
                Sorry! Unable to add Image with dimension more than {width} X{height}
              </span>
            )}

            {fileFormatError && !error && (
              <span className='size-exceeded mt-3'>
                Please add images with supported file formats
              </span>
            )}
          </div>
        </div>
        <div className='col-8 d-flex flex-column justify-content-end'>
          {!isCustomImage && (
            <>
              <div className='d-flex px-2'>
                <div className='image-details label'>Ratio :</div>
                <div className='image-details'>{ratio}</div>
              </div>
              <div className='d-flex px-2'>
                <div className='image-details label'>Size :</div>
                <div className='image-details'>
                  {width} X {height}
                  {matrix} {imageDetail}
                </div>
              </div>
            </>
          )}
          {isCustomImage && (
            <>
              <div className='d-flex px-2'>
                <div className='image-details label'>Format :</div>
                <div className='image-details'>{format}</div>
              </div>
              <div className='d-flex px-2'>
                <div className='image-details label'>Size :</div>
                <div className='image-details'>10MB</div>
              </div>
            </>
          )}
          <div>
            {imgURL && props.hasOwnProperty('shadowOverlay') && (
              <div className='px-2 image-details'>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={shadowOverlay}
                      onChange={(e) => onShadowOverlaySelect(e.target.checked)}
                    />
                  }
                  label={'Shadow Overlay'}
                />
              </div>
            )}
          </div>
          {imgURL && (
            <div onClick={removeImage} className='px-2 mt-2 pointer image-details text-underlined'>
              Remove Image
            </div>
          )}
        </div>
      </div>
      {uploadError ? (
        <></>
      ) : (
        error && (
          <div>
            <p className='error-text'>Image required</p>
          </div>
        )
      )}
    </div>
  );
};

export default ImagePicker;
